import React, { useState, useEffect, useCallback } from 'react';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useToast } from '../../hooks/use-toast';
import { 
  getContactInfo, updateContactInfo,
  getSocialMedia, createSocialMedia, updateSocialMedia, deleteSocialMedia,
  getTermsConditions, createTermsConditions, updateTermsConditions, deleteTermsConditions
} from '../../services/api';
import { ContactInfoSection } from './contact/ContactInfoSection';
import { SocialMediaSection } from './contact/SocialMediaSection';
import { TermsSection } from './contact/TermsSection';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactManager = () => {
  const { toast } = useToast();
  
  const [contactInfo, setContactInfo] = useState({ email: '', phone: '', location: '' });
  const [isEditingContact, setIsEditingContact] = useState(false);
  
  const [socialMedia, setSocialMedia] = useState([]);
  const [isAddingSocial, setIsAddingSocial] = useState(false);
  const [editingSocial, setEditingSocial] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, social: null });
  const [newSocial, setNewSocial] = useState({ platform: '', url: '', active: true, order: 0 });
  
  const [termsList, setTermsList] = useState([]);
  const [isAddingTerms, setIsAddingTerms] = useState(false);
  const [editingTerms, setEditingTerms] = useState(null);
  const [deleteTermsModal, setDeleteTermsModal] = useState({ isOpen: false, terms: null });
  const [uploadingTerms, setUploadingTerms] = useState(false);
  const [newTerms, setNewTerms] = useState({ title: '', url: '', active: true });
  
  const [loading, setLoading] = useState(true);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      const [contactRes, socialRes, termsRes] = await Promise.all([
        getContactInfo(), getSocialMedia(), getTermsConditions()
      ]);
      setContactInfo(contactRes.data);
      setSocialMedia(socialRes.data);
      setTermsList(termsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Error', description: 'Failed to load contact data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  // Handlers
  const handleSaveContactInfo = async () => {
    try {
      await updateContactInfo(contactInfo);
      toast({ title: 'Success', description: 'Contact information updated successfully' });
      setIsEditingContact(false);
      fetchAllData();
    } catch { toast({ title: 'Error', description: 'Failed to update contact information', variant: 'destructive' }); }
  };

  const handleAddSocial = async () => {
    try {
      await createSocialMedia(newSocial);
      toast({ title: 'Success', description: 'Social media link added successfully' });
      setIsAddingSocial(false);
      setNewSocial({ platform: '', url: '', active: true, order: 0 });
      fetchAllData();
    } catch { toast({ title: 'Error', description: 'Failed to add social media link', variant: 'destructive' }); }
  };

  const handleUpdateSocial = async (id, data) => {
    try {
      await updateSocialMedia(id, data);
      toast({ title: 'Success', description: 'Social media link updated' });
      setEditingSocial(null);
      fetchAllData();
    } catch { toast({ title: 'Error', description: 'Failed to update social media link', variant: 'destructive' }); }
  };

  const handleDeleteSocial = async () => {
    try {
      await deleteSocialMedia(deleteModal.social.id);
      toast({ title: 'Success', description: 'Social media link deleted' });
      fetchAllData();
    } catch { toast({ title: 'Error', description: 'Failed to delete social media link', variant: 'destructive' }); }
  };

  const handleTermsFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingTerms(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setNewTerms({ ...newTerms, url: response.data.url });
      toast({ title: 'Success', description: 'Document uploaded successfully' });
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Error', description: 'Failed to upload document', variant: 'destructive' });
    } finally { setUploadingTerms(false); }
  };

  const handleAddTerms = async () => {
    try {
      await createTermsConditions(newTerms);
      toast({ title: 'Success', description: 'Terms & Conditions added' });
      setIsAddingTerms(false);
      setNewTerms({ title: '', url: '', active: true });
      fetchAllData();
    } catch { toast({ title: 'Error', description: 'Failed to add Terms & Conditions', variant: 'destructive' }); }
  };

  const handleUpdateTerms = async (id, data) => {
    try {
      await updateTermsConditions(id, data);
      toast({ title: 'Success', description: 'Terms & Conditions updated' });
      setEditingTerms(null);
      fetchAllData();
    } catch { toast({ title: 'Error', description: 'Failed to update Terms & Conditions', variant: 'destructive' }); }
  };

  const handleDeleteTerms = async () => {
    try {
      await deleteTermsConditions(deleteTermsModal.terms.id);
      toast({ title: 'Success', description: 'Terms & Conditions deleted' });
      fetchAllData();
    } catch { toast({ title: 'Error', description: 'Failed to delete Terms & Conditions', variant: 'destructive' }); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-white text-xl">Loading...</p></div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400 mb-2">
          Contact Management
        </h2>
        <p className="text-gray-400">Manage contact information, social media links, and legal documents</p>
      </div>

      <ContactInfoSection
        contactInfo={contactInfo} setContactInfo={setContactInfo}
        isEditing={isEditingContact} setIsEditing={setIsEditingContact}
        onSave={handleSaveContactInfo}
        onCancel={() => { setIsEditingContact(false); fetchAllData(); }}
      />

      <SocialMediaSection
        socialMedia={socialMedia}
        editingSocial={editingSocial} setEditingSocial={setEditingSocial}
        isAddingSocial={isAddingSocial} setIsAddingSocial={setIsAddingSocial}
        newSocial={newSocial} setNewSocial={setNewSocial}
        onAdd={handleAddSocial} onUpdate={handleUpdateSocial}
        onDelete={(social) => setDeleteModal({ isOpen: true, social })}
      />

      <TermsSection
        termsList={termsList}
        editingTerms={editingTerms} setEditingTerms={setEditingTerms}
        isAddingTerms={isAddingTerms} setIsAddingTerms={setIsAddingTerms}
        newTerms={newTerms} setNewTerms={setNewTerms}
        uploadingTerms={uploadingTerms} onFileUpload={handleTermsFileUpload}
        onAdd={handleAddTerms} onUpdate={handleUpdateTerms}
        onDelete={(terms) => setDeleteTermsModal({ isOpen: true, terms })}
      />

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, social: null })}
        onConfirm={handleDeleteSocial}
        title="Confirm Delete"
        message={`Are you sure you want to delete the ${deleteModal.social?.platform} link? This action cannot be undone.`}
        confirmText="Delete" cancelText="Cancel"
      />
      <ConfirmModal
        isOpen={deleteTermsModal.isOpen}
        onClose={() => setDeleteTermsModal({ isOpen: false, terms: null })}
        onConfirm={handleDeleteTerms}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${deleteTermsModal.terms?.title}"? This action cannot be undone.`}
        confirmText="Delete" cancelText="Cancel"
      />
    </div>
  );
};
